import type { HexCoord } from "./types";

export function coordKey(row: number, col: number): string {
	return `${row},${col}`;
}

export function parseCoordKey(key: string): HexCoord {
	const [row, col] = key.split(",").map(Number);
	return { row: row ?? 0, col: col ?? 0 };
}

export function getNeighbors(
	row: number,
	col: number,
): Array<{ row: number; col: number }> {
	const isOddRow = (row + 1) % 2 === 1;
	if (isOddRow) {
		return [
			{ row: row - 1, col },
			{ row: row - 1, col: col - 1 },
			{ row, col: col - 1 },
			{ row, col: col + 1 },
			{ row: row + 1, col },
			{ row: row + 1, col: col - 1 },
		];
	}
	return [
		{ row: row - 1, col },
		{ row: row - 1, col: col + 1 },
		{ row, col: col - 1 },
		{ row, col: col + 1 },
		{ row: row + 1, col },
		{ row: row + 1, col: col + 1 },
	];
}

export function isBackwardMove(
	currentRow: number,
	currentCol: number,
	neighborRow: number,
	neighborCol: number,
): boolean {
	return (
		(neighborRow === currentRow - 1 && neighborCol === currentCol - 1) ||
		(neighborRow === currentRow && neighborCol === currentCol - 1) ||
		(neighborRow === currentRow + 1 && neighborCol === currentCol - 1)
	);
}

export function isForwardMove(
	currentRow: number,
	currentCol: number,
	neighborRow: number,
	neighborCol: number,
): boolean {
	const isOddRow = (currentRow + 1) % 2 === 1;
	if (isOddRow) {
		return (
			(neighborRow === currentRow - 1 && neighborCol === currentCol) ||
			(neighborRow === currentRow && neighborCol === currentCol + 1) ||
			(neighborRow === currentRow + 1 && neighborCol === currentCol)
		);
	}
	return (
		(neighborRow === currentRow - 1 && neighborCol === currentCol + 1) ||
		(neighborRow === currentRow && neighborCol === currentCol + 1) ||
		(neighborRow === currentRow + 1 && neighborCol === currentCol + 1)
	);
}

export function hexDistance(
	row1: number,
	col1: number,
	row2: number,
	col2: number,
): number {
	const cube1 = offsetHexToCube(row1, col1);
	const cube2 = offsetHexToCube(row2, col2);
	return Math.max(
		Math.abs(cube1.x - cube2.x),
		Math.abs(cube1.y - cube2.y),
		Math.abs(cube1.z - cube2.z),
	);
}

/**
 * Convert this codebase's row-offset grid into cube coordinates so distance
 * calculations stay O(1) instead of performing a BFS walk per lookup.
 */
function offsetHexToCube(
	row: number,
	col: number,
): {
	x: number;
	y: number;
	z: number;
} {
	const x = -col - Math.floor((row + (row & 1)) / 2);
	const z = row;
	const y = -x - z;
	return { x, y, z };
}

export type HexDirection =
	| "upRight"
	| "right"
	| "downRight"
	| "upLeft"
	| "left"
	| "downLeft";

export function getDirection(
	currentRow: number,
	currentCol: number,
	nextRow: number,
	nextCol: number,
): HexDirection {
	const rowDiff = nextRow - currentRow;
	const colDiff = nextCol - currentCol;
	const isOddRow = (currentRow + 1) % 2 === 1;

	if (rowDiff === 0 && colDiff === 1) {
		return "right";
	}
	if (rowDiff === 0 && colDiff === -1) {
		return "left";
	}

	if (!isOddRow) {
		if (rowDiff === -1 && colDiff === 0) {
			return "upLeft";
		}
		if (rowDiff === -1 && colDiff === -1) {
			return "upLeft";
		}
		if (rowDiff === 1 && colDiff === 0) {
			return "downLeft";
		}
		if (rowDiff === 1 && colDiff === -1) {
			return "downLeft";
		}
	} else {
		if (rowDiff === -1 && colDiff === 0) {
			return "upRight";
		}
		if (rowDiff === -1 && colDiff === 1) {
			return "upRight";
		}
		if (rowDiff === 1 && colDiff === 0) {
			return "downRight";
		}
		if (rowDiff === 1 && colDiff === 1) {
			return "downRight";
		}
	}

	if (rowDiff < 0 && colDiff > 0) {
		return "upRight";
	}
	if (rowDiff < 0 && colDiff <= 0) {
		return "upLeft";
	}
	if (rowDiff > 0 && colDiff > 0) {
		return "downRight";
	}
	if (rowDiff > 0 && colDiff <= 0) {
		return "downLeft";
	}
	return "right";
}

export function areHexAdjacent(a: HexCoord, b: HexCoord): boolean {
	return hexDistance(a.row, a.col, b.row, b.col) === 1;
}

/** Shortest walk between two cells on the offset hex grid (includes endpoints). */
export function shortestHexPath(from: HexCoord, to: HexCoord): HexCoord[] {
	if (from.row === to.row && from.col === to.col) {
		return [from];
	}

	const endKey = coordKey(to.row, to.col);
	const queue: Array<{ row: number; col: number; path: HexCoord[] }> = [
		{ row: from.row, col: from.col, path: [from] },
	];
	const visited = new Set<string>([coordKey(from.row, from.col)]);

	while (queue.length > 0) {
		const current = queue.shift();
		if (!current) {
			break;
		}
		const key = coordKey(current.row, current.col);
		if (key === endKey) {
			return current.path;
		}
		for (const neighbor of getNeighbors(current.row, current.col)) {
			const nKey = coordKey(neighbor.row, neighbor.col);
			if (visited.has(nKey)) {
				continue;
			}
			visited.add(nKey);
			queue.push({
				row: neighbor.row,
				col: neighbor.col,
				path: [...current.path, neighbor],
			});
		}
	}

	return [from, to];
}

/** Add every tile on shortest paths between consecutive coordinates. */
export function expandPathCoordCorridor(coords: HexCoord[]): Set<string> {
	const set = new Set<string>();
	for (let i = 0; i < coords.length; i++) {
		const current = coords[i];
		if (!current) {
			continue;
		}
		set.add(coordKey(current.row, current.col));
		const next = coords[i + 1];
		if (!next) {
			continue;
		}
		for (const step of shortestHexPath(current, next)) {
			set.add(coordKey(step.row, step.col));
		}
	}
	return set;
}

export function isValidPath(path: HexCoord[]): boolean {
	const pathSet = new Set(path.map((p) => coordKey(p.row, p.col)));

	for (let i = 0; i < path.length; i++) {
		const current = path[i];
		if (!current) {
			continue;
		}
		const neighbors = getNeighbors(current.row, current.col);
		const adjacentInPath = neighbors.filter((n) =>
			pathSet.has(coordKey(n.row, n.col)),
		);
		const expectedAdjacent = i === 0 || i === path.length - 1 ? 1 : 2;
		if (adjacentInPath.length !== expectedAdjacent) {
			return false;
		}
	}
	return true;
}
